import React, { memo, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Venue } from '../types'
import { DEAL_TYPE_COLORS, DEAL_TYPE_LABELS, CATEGORY_LABELS } from '../types'
import { fmtTime, isVenueActiveNow, isVenueOpenForBusiness, getEffectiveVenueStatus, getTodayDOW, downgradeForClosedDoors } from '../utils/filters'
import { getScheduleStatus, STATUS_VISUALS } from '../utils/happeningNow'
import type { HappyHourStatus, ScheduleStatus } from '../utils/happeningNow'
import { Analytics, track } from '../services/analytics'
import { trackImpression, hasTrackedImpression } from '../services/impressionBuffer'
import { openExternal } from '../utils/openExternal'
import { fmtDealValue } from '../utils/dealFormat'
function formatDays(days: string[]): string {
  if (!days || days.length === 0) return ''
  if (days.length === 7) return 'Daily'
  const ALL = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
  const sorted = [...days].sort((a, b) => ALL.indexOf(a) - ALL.indexOf(b))
  if (sorted.length === 5 && ['Mon','Tue','Wed','Thu','Fri'].every(d => sorted.includes(d))) return 'Mon–Fri'
  if (sorted.length === 6 && ['Mon','Tue','Wed','Thu','Fri'].every(d => sorted.includes(d))) {
    return sorted.includes('Sat') ? 'Mon–Sat' : 'Mon–Sun'
  }
  if (sorted.length === 2 && sorted[0] === 'Sat' && sorted[1] === 'Sun') return 'Weekends'
  const indices = sorted.map(d => ALL.indexOf(d))
  const isConsec = indices.every((v, i) => i === 0 || v === indices[i-1] + 1)
  if (isConsec && sorted.length > 2) return `${sorted[0]}–${sorted[sorted.length - 1]}`
  return sorted.join(', ')
}

const STATUS_PRIORITY: HappyHourStatus[] = ['live_now','ends_soon','starts_soon','later_today','ended','not_today']
// NOTE: impression dedup moved into impressionBuffer (hasTrackedImpression) —
// a private Set here couldn't be reset when the app resumes after a long
// background gap, which froze card_view counting for the life of the process.

// Does this deal have a directly comparable dollar price? Used to keep
// discount deals ("$2 off") out of price-based orderings — a $2-off deal
// sorting as if it cost $2 would rank a discounted $14 cocktail above a
// real $4 beer, which is backwards for "cheapest/best deal first" lists.
function hasComparablePrice(deal: any): boolean {
  const vt = deal.value_type === 'amount_off' || deal.value_type === 'percent_off' ? deal.value_type : 'price'
  return vt === 'price' && (deal.price ?? deal.value) != null
}
function comparablePrice(deal: any): number {
  return deal.price ?? deal.value ?? 0
}

function fmtMins(mins: number): string {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 15.5S2 11.1 2 6.5C2 4.6 3.6 3 5.5 3c1.2 0 2.3.6 3 1.5C9.2 3.6 10.3 3 11.5 3 13.4 3 15 4.6 15 6.5c0 4.6-6 9-6 9z"
        fill={filled ? '#E24B4A' : 'none'} stroke={filled ? '#E24B4A' : 'currentColor'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M14 11.3c0 .3-.1.6-.2.9-.1.3-.3.5-.5.7-.4.4-.8.6-1.3.6-.3 0-.7-.1-1-.2-.3-.1-.6-.3-.9-.5l-.8-.5c-.9-.6-1.7-1.3-2.4-2-.7-.7-1.4-1.5-2-2.4l-.5-.8c-.2-.3-.4-.6-.5-.9-.1-.3-.2-.7-.2-1 0-.5.2-.9.5-1.3.2-.2.4-.4.7-.5.3-.1.6-.2.9-.2.1 0 .2 0 .3.1.1.1.2.2.3.4l1.1 1.5c.1.1.2.3.2.4 0 .1 0 .3-.1.4-.1.1-.1.2-.2.3L7 7.1c.5.9 1 1.5 1.9 2l.5-.5c.1-.1.3-.2.4-.3.1-.1.3-.1.4-.1.1 0 .3.1.4.2l1.5 1.1c.2.1.3.2.4.3 0 .1.1.3 0 .5z"
        stroke="currentColor" strokeWidth="1.2" fill="none"/>
    </svg>
  )
}

function DirectionsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M8 1L15 8L8 15L1 8L8 1Z" stroke="currentColor" strokeWidth="1.4" fill="none"/>
      <path d="M5.5 8H10.5M10.5 8L8.5 6M10.5 8L8.5 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="12" cy="3" r="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <circle cx="12" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <circle cx="3" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="4.3" y1="7.2" x2="10.7" y2="3.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="4.3" y1="8.8" x2="10.7" y2="12.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  )
}

const CATEGORY_ICONS: Partial<Record<string, string>> = {
  rooftop:      '🏙️',
  patio:        '☀️',
  dog_friendly: '🐾',
  sports_bar:   '🏈',
  brewery:      '🍺',
  wine_bar:     '🍷',
  cocktail_bar: '🍸',
  live_music:   '🎵',
  date_night:   '🥂',
  late_night:   '🌙',
  dive_bar:     '🍻',
  waterfront:   '🌊',
}

interface VenueCardProps {
  venue: Venue
  isFavorite: boolean
  onToggleFavorite: (venueId: string, venueName: string) => void
  showDayContext?:  boolean   // show Today/Deals-on labels — browse page only
  isSelected?: boolean
  distanceLabel?: string
  cardRef?: (el: HTMLDivElement | null) => void
  isTrending?: boolean
}

export const VenueCard = memo(function VenueCard({
  venue, isFavorite, onToggleFavorite,
  showDayContext = false,
  isSelected = false, distanceLabel, cardRef, isTrending = false,
}: VenueCardProps) {
  const navigate = useNavigate()
  const isOpen = isVenueActiveNow(venue)
  const schedules = venue.schedules || []
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const setRef = useCallback((el: HTMLDivElement | null) => {
    if (cardRef) cardRef(el)
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    if (!el || hasTrackedImpression(venue.id, 'card_view')) return
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        timerRef.current = setTimeout(() => {
          // trackImpression is itself session-deduped — safe to call
          trackImpression(venue.id, 'card_view')
        }, 1000)
      } else {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
      }
    }, { threshold: 0.5 })
    observerRef.current.observe(el)
  }, [venue.id, cardRef]) // eslint-disable-line

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const venueStatus: ScheduleStatus | null = (() => {
    const statuses = schedules
      .map(s => getScheduleStatus(s))
      .filter((s): s is ScheduleStatus => s !== null)
    if (!statuses.length) return null
    const best = statuses.sort((a, b) =>
      STATUS_PRIORITY.indexOf(a.status) - STATUS_PRIORITY.indexOf(b.status)
    )[0]
    // Downgrade live_now/ends_soon if venue isn't physically open yet
    if (
      best &&
      !isVenueOpenForBusiness(venue) &&
      (best.status === 'live_now' || best.status === 'ends_soon')
    ) {
      return downgradeForClosedDoors(venue, best)
    }
    return best
  })()

  const bestSchedule = venueStatus?.schedule ?? schedules[0]
  const vis = venueStatus ? STATUS_VISUALS[venueStatus.status] : null

  const DEAL_ORDER = ['beer', 'cocktail', 'liquor', 'wine', 'food', 'general']
  const topDeals = [...(bestSchedule?.deals ?? [])]
    .sort((a, b) => {
      const aIdx = DEAL_ORDER.indexOf(a.type)
      const bIdx = DEAL_ORDER.indexOf(b.type)
      if (aIdx !== bIdx) return aIdx - bIdx
      // Only deals with a real comparable PRICE participate in price
      // ordering — a "$2 off" deal is not a $2 item, so it must not
      // out-rank a genuine $4 beer just because 2 < 4 numerically.
      const aHasPrice = hasComparablePrice(a)
      const bHasPrice = hasComparablePrice(b)
      if (aHasPrice && !bHasPrice) return -1
      if (!aHasPrice && bHasPrice) return 1
      if (aHasPrice && bHasPrice) return comparablePrice(a) - comparablePrice(b)
      return 0
    })
    .slice(0, 4)

  const URGENCY_MINS = 90
  const showEndsIn = venueStatus?.minutesRemaining != null && venueStatus.minutesRemaining <= URGENCY_MINS
  const showStartsIn = venueStatus?.minutesUntil != null && venueStatus.minutesUntil <= URGENCY_MINS

  // Category badges — skip generic ones, max 3
  const SKIP_CATS = new Set(['restaurant', 'pub', 'lounge', 'dive_bar', 'date_night', 'late_night'])
  const categoryBadges = (venue.categories ?? []).filter(c => !SKIP_CATS.has(c)).slice(0, 3)
  const showDogBadge = venue.dog_friendly && !venue.categories?.includes('dog_friendly' as any)

  function handleCardClick() {
    track('venue_card_clicked', { venue_id: venue.id, venue_name: venue.name, is_open: isOpen, is_featured: venue.is_featured ?? false })
    navigate(`/venue/${venue.id}`)
  }

  function handleFavorite(e: React.MouseEvent) {
    e.stopPropagation()
    onToggleFavorite(venue.id, venue.name)
  }

  function handlePhone(e: React.MouseEvent) {
    e.stopPropagation()
    if (venue.phone) {
      // Analytics.phoneClicked increments venue_stats.phone_clicks —
      // the raw track() call it replaces wrote nowhere, so card-level
      // phone taps were invisible in venue-facing stats.
      Analytics.phoneClicked(venue.id, venue.name)
      window.location.href = `tel:${venue.phone}`
    }
  }

  function handleDirections(e: React.MouseEvent) {
    e.stopPropagation()
    if (venue.address) {
      Analytics.getDirectionsClicked(venue.id, venue.name)
      openExternal(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`)
    }
  }

  function handleShare(e: React.MouseEvent) {
    e.stopPropagation()
    const url = `${window.location.origin}/venue/${venue.id}`
    if (navigator.share) {
      navigator.share({ title: venue.name, text: `Check out happy hour at ${venue.name} 🍺`, url })
    } else {
      navigator.clipboard.writeText(url)
    }
    track('venue_shared', { venue_id: venue.id, venue_name: venue.name })
  }

  return (
    <div
      ref={setRef}
      className={`vc${isOpen ? ' vc--open' : ''}${isSelected ? ' vc--selected' : ''}${venue.is_featured ? ' vc--featured' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleCardClick()}
      aria-label={`${venue.name} — ${venueStatus?.badge ?? 'See details'}`}
    >

      {/* ROW 1: Status badge + heart */}
      <div className="vc__top">
        <div className="vc__badges-left">
          {venueStatus && vis && (
            <span className="vc__badge vc__badge--status" style={{ background: vis.bg, color: vis.text, borderColor: vis.border }}>
              <span className={`vc__status-dot${vis.pulse ? ' vc__status-dot--pulse' : ''}`} style={{ background: vis.dot }} />
              {venueStatus.badge}
            </span>
          )}
          {(venue as any).is_sponsored && (
            <span className="vc__badge vc__badge--sponsored">◆ Sponsored</span>
          )}
          {venue.is_featured && (
            <span className="vc__badge vc__badge--featured">⭐ Featured</span>
          )}
          {isTrending && (
            <span className="vc__badge vc__badge--trending">🔥 Trending</span>
          )}
        </div>
        <button className={`vc__heart${isFavorite ? ' vc__heart--saved' : ''}`} onClick={handleFavorite} aria-label="Save">
          <HeartIcon filled={isFavorite} />
        </button>
      </div>

      {/* ROW 2: Venue name */}
      <div className="vc__name">{venue.name}</div>

      {/* ROW 3: Meta */}
      <div className="vc__meta">
        <span className="vc__neighborhood">{venue.neighborhood}</span>
        {venue.price_tier && <><span className="vc__meta-dot">·</span><span className="vc__price">{venue.price_tier}</span></>}
        {distanceLabel && <><span className="vc__meta-dot">·</span><span className="vc__distance">{distanceLabel}</span></>}
        {venue.last_verified_at && (
          <span className="vc__verified">✓ Verified</span>
        )}
      </div>

      {/* ROW 4: Category badges */}
      {(categoryBadges.length > 0 || showDogBadge) && (
        <div className="vc__categories">
          {categoryBadges.map(cat => (
            <span key={cat} className="vc__cat-badge">
              {CATEGORY_ICONS[cat] && <span className="vc__cat-icon">{CATEGORY_ICONS[cat]}</span>}
              {CATEGORY_LABELS[cat] ?? cat}
            </span>
          ))}
          {showDogBadge && (
            <span className="vc__cat-badge"><span className="vc__cat-icon">🐾</span>Dog Friendly</span>
          )}
        </div>
      )}

      {/* ROW 5: Time — contextual day label (browse page) or simple time (elsewhere) */}
      {bestSchedule && (() => {
        if (!showDayContext) {
          // Simple display for Now page, Best Picks, etc.
          return (
            <div className="vc__time-row">
              <span className="vc__time">
                {bestSchedule.is_all_day
                  ? 'All day'
                  : `${fmtTime(bestSchedule.start_time)} – ${fmtTime(bestSchedule.end_time)}`
                }
              </span>
              {showEndsIn && venueStatus?.minutesRemaining != null && (
                <span className="vc__urgency-pill vc__urgency-pill--ends">ends in {fmtMins(venueStatus.minutesRemaining)}</span>
              )}
              {!showEndsIn && showStartsIn && venueStatus?.minutesUntil != null && (
                <span className="vc__urgency-pill vc__urgency-pill--starts">starts in {fmtMins(venueStatus.minutesUntil)}</span>
              )}
            </div>
          )
        }

        // Browse page: contextual Today / Deals-on display
        const todayDOW   = getTodayDOW()
        // "Today" must agree with the badge: if the effective status is
        // not_today (including doors-closed-all-day downgrades), the card
        // must not simultaneously claim "Today · 4 PM – 8 PM". Falls back
        // to the "Deals on <days>" branch instead.
        const hasToday   = schedules.some(s => s.days.includes(todayDOW)) &&
                           venueStatus?.status !== 'not_today'
        const allDays    = [...new Set(schedules.flatMap(s => s.days))]
        const extraCount = schedules.length - 1

        return (
          <>
            <div className="vc__time-row">
              {hasToday ? (
                <span className="vc__time">
                  {bestSchedule.is_all_day
                    ? 'Today · All day'
                    : `Today · ${fmtTime(bestSchedule.start_time)} – ${fmtTime(bestSchedule.end_time)}`
                  }
                </span>
              ) : (
                <span className="vc__time vc__time--inactive">
                  {schedules.length > 1 && allDays.length === schedules.length
                    ? `Daily deals · ${formatDays(allDays)}`
                    : `Deals on ${formatDays(allDays)}`
                  }
                </span>
              )}
              {showEndsIn && venueStatus?.minutesRemaining != null && (
                <span className="vc__urgency-pill vc__urgency-pill--ends">ends in {fmtMins(venueStatus.minutesRemaining)}</span>
              )}
              {!showEndsIn && showStartsIn && venueStatus?.minutesUntil != null && (
                <span className="vc__urgency-pill vc__urgency-pill--starts">starts in {fmtMins(venueStatus.minutesUntil)}</span>
              )}
            </div>
            {extraCount > 0 && (() => {
              const uniqueTimes = new Set(
                schedules.map(s => s.is_all_day ? 'allday' : `${s.start_time}-${s.end_time}`)
              )
              const timesVary = uniqueTimes.size > 1
              return (
                <div className="vc__multi-schedule-hint">
                  {timesVary
                    ? `+${extraCount} more · hours & deals vary by day`
                    : `Deals change daily · tap for full schedule`
                  }
                </div>
              )
            })()}
          </>
        )
      })()}

      {/* ROW 6: Deal pills */}
      {topDeals.length > 0 ? (
        <div className="vc__deals">
          {topDeals.map((deal, i) => {
            const valueLabel = fmtDealValue(deal)
            return (
              <span key={i} className="vc__deal-pill"
                style={{ background: DEAL_TYPE_COLORS[deal.type].bg, color: DEAL_TYPE_COLORS[deal.type].text }}>
                <span className="vc__pill-type">{DEAL_TYPE_LABELS[deal.type]}</span>
                <span className="vc__pill-price">
                  {valueLabel ?? (deal.description.length > 14 ? deal.description.slice(0, 14) + '…' : deal.description)}
                </span>
              </span>
            )
          })}
          {(bestSchedule?.deals ?? []).length > 4 && (
            <span className="vc__more-deals">+{(bestSchedule?.deals ?? []).length - 4} more</span>
          )}
        </div>
      ) : bestSchedule?.deal_text ? (
        <div className="vc__deal-text">{bestSchedule.deal_text}</div>
      ) : null}

      {/* ROW 7: Footer */}
      <div className="vc__footer">
        <span className="vc__cta">View deals →</span>
        <div className="vc__actions">
          {venue.phone && (
            <button className="vc__action-btn" onClick={handlePhone} aria-label="Call" title="Call">
              <PhoneIcon />
            </button>
          )}
          {venue.address && (
            <button className="vc__action-btn" onClick={handleDirections} aria-label="Directions" title="Directions">
              <DirectionsIcon />
            </button>
          )}
          <button className="vc__action-btn" onClick={handleShare} aria-label="Share" title="Share">
            <ShareIcon />
          </button>
        </div>
      </div>

    </div>
  )
})
